import type { LocaleResources, ResourceMap, SupportedLocale } from '@hostesa/i18n';
import * as analyticsI18n from '../routes/analytics/i18n';
// Flat-shape modules export `resources = { en, bg, de, fr }` where each value is
// the raw namespace object; wrapped-shape modules export `resources` already
// keyed `{ [locale]: { [namespace]: {...} } }`.
import * as bookingsI18n from '../routes/bookings/i18n';
import callsI18n from '../routes/calls/i18n';
import * as hotelSetupI18n from '../routes/hotel-setup/i18n';
import * as integrationsI18n from '../routes/integrations/i18n';
import * as knowledgeI18n from '../routes/knowledge/i18n';
import * as onboardingI18n from '../routes/onboarding/i18n';
import * as operationsI18n from '../routes/operations/i18n';
import receptionI18n from '../routes/reception/i18n';
import * as reservationsI18n from '../routes/reservations/i18n';
import * as scriptsI18n from '../routes/scripts/i18n';
import * as settingsI18n from '../routes/settings/i18n';
import * as teamI18n from '../routes/team/i18n';
import * as usageI18n from '../routes/usage/i18n';

/**
 * App-level i18n resources for the platform shell, merged on top of the
 * bundled `@hostesa/i18n` `common` namespace via the `platform` namespace.
 *
 * Every key MUST exist in all four supported locales — `@hostesa/i18n` throws
 * on a missing key in `test` and `production` modes (key-parity enforcement),
 * so adding a string here without a translation will fail the build/tests.
 * Non-`en` locales start as English placeholders where a real translation is
 * not yet available; this is the documented "temporary" state.
 */

const en = {
  appName: 'Hostesa',
  // Shown next to a destructive/sensitive control that is disabled because the
  // current session is an operator impersonation session.
  impersonationRestriction:
    'Disabled during an impersonation session — sensitive changes are blocked while acting as this user.',
  shell: {
    hotelSwitcher: 'Switch hotel',
    signedInAs: 'Signed in as',
    timezone: 'Hotel time',
    openNav: 'Open navigation',
    liveCall: 'Live call',
  },
  navGroups: {
    reception: 'Reception',
    hospitality: 'Hospitality',
    intelligence: 'Intelligence',
    settings: 'Settings',
  },
  nav: {
    reception: 'Reception',
    liveCalls: 'Live calls',
    calls: 'Calls',
    conversations: 'Conversations',
    reservations: 'Reservations',
    tasks: 'Tasks',
    bookings: 'Bookings',
    knowledge: 'Knowledge',
    onboarding: 'Website onboarding',
    scripts: 'Script Studio',
    handoffRules: 'Handoff rules',
    integrations: 'Integrations',
    analytics: 'Analytics',
    usage: 'Usage',
    settings: 'Settings',
    hotelSetup: 'Hotel setup',
    hotelProfile: 'Hotel profile',
    team: 'Team',
  },
  hotelProfile: {
    eyebrow: 'Settings',
    title: 'Hotel profile',
    description: "Your hotel's identity — name, languages, timezone, and status.",
    loading: 'Loading hotel profile…',
    errorTitle: 'Could not load the hotel',
    errorDescription: 'Something went wrong loading this hotel. Please try again.',
    fields: {
      name: 'Hotel name',
      slug: 'Slug',
      slugHint: 'The stable identifier in URLs — it cannot be changed.',
      timezone: 'Timezone',
      languages: 'Languages',
      languagesHint: 'Languages the AI receptionist speaks. At least one is required.',
      defaultLanguage: 'Default language',
      status: 'Status',
      operationsAgent: 'Operations agent',
    },
    status: { draft: 'Draft', active: 'Active', suspended: 'Suspended' },
    operationsAgent: { enabled: 'Enabled', disabled: 'Disabled' },
    save: 'Save changes',
    saving: 'Saving…',
    saved: 'Hotel profile saved.',
    saveError: 'Could not save changes. Please try again.',
    readOnlyNote: 'You have read-only access to this hotel.',
    knowledgeLink:
      'Manage the AI-facing hotel profile (description, amenities, policies) in Knowledge.',
  },
  pages: {
    reception: {
      title: 'Reception',
      description: 'A calm overview of activity across the front desk.',
    },
    liveCalls: { title: 'Live calls', description: 'Calls in progress right now.' },
    calls: { title: 'Calls', description: 'Completed and missed call history.' },
    reservations: {
      title: 'Reservations',
      description: 'Reservation requests and offer drafts.',
    },
    tasks: { title: 'Tasks', description: 'In-stay service requests and exceptions.' },
    settings: { title: 'Settings', description: 'Hotel configuration and preferences.' },
  },
  commandPalette: {
    title: 'Command palette',
    triggerLabel: 'Search',
    placeholder: 'Search pages…',
    empty: 'No results.',
  },
  login: {
    title: 'Sign in',
    subtitle: 'Welcome back to your hotel operations desk.',
    submitting: 'Signing in…',
    emailRequired: 'Enter your email address.',
    emailInvalid: 'Enter a valid email address.',
    passwordRequired: 'Enter your password.',
    brandTagline: 'The multilingual AI receptionist that never sleeps.',
    brandSupport:
      'Answer every call, in every language, and turn enquiries into bookings — your front desk, always on.',
  },
  noAccess: {
    title: 'No access',
    description:
      'This page is not available for your account. If you believe this is a mistake, contact your hotel administrator.',
    backToReception: 'Back to reception',
  },
  notFound: {
    title: 'Page not found',
    description: 'We could not find what you were looking for.',
    backToReception: 'Back to reception',
  },
  onboarding: {
    title: 'No hotel linked yet',
    description:
      'Your account is not linked to a hotel. Ask your Hostesa contact to finish provisioning your hotel, then sign in again.',
    operatorTitle: 'You are signed in as a Hostesa operator',
    operatorDescription:
      'Hotel dashboards are per-hotel. Operator work — partner provisioning, oversight, impersonation — lives in the back-office.',
    openBackoffice: 'Open the back-office',
  },
  noHotel: {
    title: 'No hotel available',
    description: 'Your account does not have access to any hotel yet.',
  },
  impersonation: {
    bannerLabel: 'Impersonation',
    actingAs: 'Acting as',
    exit: 'Exit impersonation',
  },
} as const;

/**
 * The full resource map. Non-English locales reuse the English copy as a
 * temporary placeholder so the key-parity check passes; translators replace
 * these strings without changing the key shape.
 */
const LOCALES: readonly SupportedLocale[] = ['en', 'bg', 'de', 'fr'];

/** A flat module: `resources[locale]` is the raw namespace object → wrap as `{ [ns]: obj }`. */
function fromFlat(ns: string, res: Record<string, unknown>): ResourceMap {
  const out: ResourceMap = {};
  for (const l of LOCALES) {
    out[l] = { [ns]: (res[l] ?? res.en) as Record<string, unknown> } as LocaleResources;
  }
  return out;
}

/** A wrapped module: `resources[locale]` is already `{ [ns]: {...} }`. */
function fromWrapped(res: Record<string, unknown>): ResourceMap {
  const out: ResourceMap = {};
  for (const l of LOCALES) {
    out[l] = (res[l] ?? res.en) as LocaleResources;
  }
  return out;
}

/** Deep-ish merge per locale (namespaces are disjoint, so a shallow per-locale merge suffices). */
function mergeResourceMaps(...maps: ResourceMap[]): ResourceMap {
  const out: ResourceMap = { en: {}, bg: {}, de: {}, fr: {} };
  for (const m of maps) {
    for (const l of LOCALES) {
      out[l] = { ...(out[l] ?? {}), ...(m[l] ?? {}) };
    }
  }
  return out;
}

/**
 * The full resource map: the platform shell namespace plus every feature slice's
 * namespace. Non-English locales reuse English copy as a temporary placeholder so
 * key-parity passes; translators replace strings without changing key shapes.
 */
export const APP_I18N_RESOURCES: ResourceMap = mergeResourceMaps(
  { en: { platform: en }, bg: { platform: en }, de: { platform: en }, fr: { platform: en } },
  fromFlat(callsI18n.namespace, callsI18n.resources),
  fromFlat(receptionI18n.namespace, receptionI18n.resources),
  fromFlat(bookingsI18n.namespace, bookingsI18n.resources),
  fromFlat(reservationsI18n.namespace, reservationsI18n.resources),
  fromWrapped(analyticsI18n.resources),
  fromWrapped(hotelSetupI18n.resources),
  fromWrapped(integrationsI18n.resources),
  fromWrapped(knowledgeI18n.resources),
  fromWrapped(onboardingI18n.resources),
  fromWrapped(operationsI18n.resources),
  fromWrapped(scriptsI18n.resources),
  fromWrapped(settingsI18n.resources),
  fromWrapped(teamI18n.resources),
  fromWrapped(usageI18n.resources),
);
